import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { useNavigate } from "@tanstack/react-router";
import { api } from "../../lib/api";
import { setAnalyticsContext } from "../../lib/analytics";
import type {
  POI,
  Layer,
  MapLabel,
  VisitorIllustration,
} from "../../components/MapCanvas";
import type { LandmarkPOI } from "@wondermaps/maps";
import type { Geometry, Point } from "geojson";

export function useVisitorMapData(orgSlug: string, mapSlug: string) {
  const navigate = useNavigate();
  const mapQuery = useQuery({
    queryKey: ["visitor-map", orgSlug, mapSlug],
    queryFn: async () => {
      const res = await api.maps.visitor[":orgSlug"][":mapSlug"].$get({
        param: { orgSlug, mapSlug },
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error("error" in error ? error.error : "Failed to fetch map");
      }
      return res.json();
    },
    // staleTime:0 + refetchOnWindowFocus so a tab that was open at flip-time
    // picks up a pause/unpause on next focus. Server LRU still absorbs load.
    // (plan 122, decision #10.)
    staleTime: 0,
    refetchOnWindowFocus: true,
  });

  const rawData = mapQuery.data;

  // Slug-history redirect: API returns { redirectTo, isRedirect } when the requested slug
  // matches a retired entry in mapSlugHistory. Navigate at the router level w/ replace.
  const isRedirect = !!rawData && "isRedirect" in rawData && rawData.isRedirect === true;
  const redirectTo = isRedirect ? (rawData as { redirectTo: string }).redirectTo : null;

  useEffect(() => {
    if (!redirectTo) return;
    // `to: "."` preserves current route shape — works for both `/$mapSlug` (subdomain)
    // and `/$orgSlug/$mapSlug` (path-based). Only the mapSlug param changes.
    void navigate({
      to: ".",
      params: (prev: Record<string, string>) => ({ ...prev, mapSlug: redirectTo }),
      replace: true,
    });
  }, [redirectTo, navigate]);

  // Paused-map gate (plan 122). The server returns a discriminated paused
  // shape when status === "paused"; consumers that read map/branding/version
  // must skip this branch. VisitorLayout reads `pausedData` to render the
  // splash before any of the downstream destructuring runs.
  const isPaused =
    !!rawData && "status" in rawData && rawData.status === "paused";
  const pausedData = isPaused
    ? (rawData as Extract<typeof rawData, { status: "paused" }>)
    : null;

  // Narrow rawData to the full payload shape (excluding redirect + paused).
  type FullMapData = Exclude<
    typeof rawData,
    { isRedirect: true } | { status: "paused" } | undefined
  >;
  const mapData: FullMapData | undefined =
    !rawData || isRedirect || isPaused ? undefined : (rawData as FullMapData);

  // Tag analytics context w/ map_status so $pageleave + downstream events
  // are segmentable. $pageview on initial nav already fired by the time data
  // lands, so this primarily catches pageleave + subsequent tab-focus refetches.
  useEffect(() => {
    if (rawData && !isRedirect) {
      setAnalyticsContext({ map_status: isPaused ? "paused" : "active" });
    }
  }, [rawData, isRedirect, isPaused]);

  // Detect if data is from cache (stale but present, not currently fetching)
  const isFromCache = !!mapData && mapQuery.isStale && !mapQuery.isFetching;

  // Transform layers
  const layers: Layer[] = useMemo(() => {
    if (!mapData) return [];
    return mapData.version.layers.map((l) => ({
      id: l.id,
      name: l.name,
      layerType: l.layerType,
      geom: l.geom as Geometry | null,
      styleJson: l.styleJson,
      sortOrder: l.sortOrder,
    }));
  }, [mapData]);

  // Transform POIs
  const pois: POI[] = useMemo(() => {
    if (!mapData) return [];
    return mapData.version.pois.map((p) => ({
      id: p.id,
      mapId: p.mapId,
      name: p.name,
      slug: ((p as Record<string, unknown>).slug as string | null) ?? null,
      nameTranslations: ((p as Record<string, unknown>).nameTranslations as Record<string, string> | null) ?? null,
      description: p.description,
      descriptionRich: p.descriptionRich,
      categoryId: p.categoryId,
      categorySlug: p.categorySlug,
      category: p.category,
      tags: p.tags ?? [],
      location: p.location as Point | null,
      propertiesJson: p.propertiesJson,
      photos: p.photos ?? [],
      status: p.status ?? null,
      statusNote: p.statusNote ?? null,
      seasonDates: (p as Record<string, unknown>).seasonDates as { start: string; end: string } | null ?? null,
      hours: p.hours ?? null,
      links: p.links ?? [],
      sponsorTier: p.sponsorTier ?? null,
      sponsorRef: (p as Record<string, unknown>).sponsorRef as POI["sponsorRef"] ?? null,
      ratingsEnabled: (p as Record<string, unknown>).ratingsEnabled as boolean ?? true,
      showNameLabel: ((p as Record<string, unknown>).showNameLabel as boolean | null) ?? null,
    }));
  }, [mapData]);

  // Convert POIs to LandmarkPOI format for wayfinding instructions
  const landmarkPOIs: LandmarkPOI[] = useMemo(() => {
    return pois
      .filter((p) => p.location)
      .map((p) => ({
        id: p.id,
        name: p.name,
        coordinates: [p.location!.coordinates[0], p.location!.coordinates[1]] as [
          number,
          number,
        ],
      }));
  }, [pois]);

  // Transform labels
  const labels: MapLabel[] = useMemo(() => {
    if (!mapData?.labels) return [];
    return mapData.labels.map((l) => ({
      id: l.id as string,
      text: l.text as string,
      location: l.location as Point | null,
      type: l.type as "region" | "fixed",
      fontSize: l.fontSize as number,
      fontWeight: l.fontWeight as "normal" | "bold",
      color: l.color as string,
      rotation: l.rotation as number,
      minZoom: l.minZoom as number,
      maxZoom: l.maxZoom as number,
      opacity: l.opacity as number,
      backgroundColor: l.backgroundColor as string | null,
      haloWidth: l.haloWidth as number | null,
      ...("sponsorId" in l && l.sponsorId
        ? {
            sponsorId: l.sponsorId as string,
            sponsorLogoUrl: (l as Record<string, unknown>).sponsorLogoUrl as string | null,
            sponsorTierColor: (l as Record<string, unknown>).sponsorTierColor as string | null,
          }
        : {}),
    }));
  }, [mapData?.labels]);

  // Categories from API
  const categories = mapData?.categories ?? [];

  // Sticker placements (plan 120, PR5). Visitor payload already inlines the
  // asset slug so the Deck overlay can resolve atlas tiles without another
  // round-trip. `assetId` is dropped here — the visitor only needs the slug.
  //
  // Numeric fallbacks are defensive: if a field somehow arrives undefined or
  // NaN (cached payload from before PR2, RPC type drift, etc.) we render with
  // sane defaults rather than NaN-tinting the IconLayer alpha to 0/255 in a
  // way that masks the real bug. Defaults match the schema (`opacity=1`,
  // `min_zoom=14`, `max_zoom=22`).
  const illustrations: VisitorIllustration[] = useMemo(() => {
    const raw = (mapData?.version as { illustrations?: unknown[] } | undefined)
      ?.illustrations;
    if (!raw) return [];
    const num = (v: unknown, fallback: number): number => {
      const n = Number(v);
      return Number.isFinite(n) ? n : fallback;
    };
    // PR9: minZoom/maxZoom are nullable. NULL preserves through to the render
    // layer where resolveStickerZoomRange falls back to theme defaults. Don't
    // clamp here — that would hide the "inherit theme default" intent.
    const numOrNull = (v: unknown): number | null => {
      if (v === null || v === undefined) return null;
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    };
    return raw.map((r) => {
      const i = r as Record<string, unknown>;
      return {
        id: i.id as string,
        assetSlug: i.assetSlug as string,
        center: i.center as VisitorIllustration["center"],
        widthMeters: num(i.widthMeters, 10),
        rotationDeg: num(i.rotationDeg, 0),
        opacity: num(i.opacity, 1),
        minZoom: numOrNull(i.minZoom),
        maxZoom: numOrNull(i.maxZoom),
        zOrderOverride:
          i.zOrderOverride === null || i.zOrderOverride === undefined
            ? null
            : num(i.zOrderOverride, 7.7),
        sortOrder: num(i.sortOrder, 0),
      };
    });
  }, [mapData]);

  return {
    mapData,
    pausedData,
    layers,
    pois,
    landmarkPOIs,
    labels,
    categories,
    illustrations,
    isFromCache,
    isError: mapQuery.isError,
    isStale: mapQuery.isStale,
    isFetching: mapQuery.isFetching,
    dataUpdatedAt: mapQuery.dataUpdatedAt,
    refetch: mapQuery.refetch,
  };
}
